/**
 * 📥 NOTIFICATION BANNER HELPERS
 * Created: January 31, 2026
 * Based on Paul's navigation decision matrix
 *
 * Purpose: Determine notification behavior and formatting for WMA in-app banners
 * Cross-Platform: Establishes pattern for iOS/Android native notifications
 */

import React from 'react';
// M1 FIX (Mission 35B): Import shared type lists — single source of truth
import { AUTO_NAVIGATE_TYPES, BANNER_TYPES } from './notificationTypes';

export type NotificationBehavior = 'auto' | 'banner' | 'silent';

/**
 * Get notification behavior based on type
 *
 * @param notificationType - Backend notification type string
 * @returns 'auto' (auto-navigate), 'banner' (show banner), 'silent' (pill only)
 */
export function getNotificationBehavior(notificationType: string): NotificationBehavior {
  if ((AUTO_NAVIGATE_TYPES as readonly string[]).includes(notificationType)) {
    return 'auto';
  }
  if ((BANNER_TYPES as readonly string[]).includes(notificationType)) {
    return 'banner';
  }
  return 'silent';
}

/**
 * Get icon for notification type.
 *
 * Returns an emoji string for most types, or a React element (CCChatBubbleIcon)
 * for Cedar chat responses. The banner components accept both.
 *
 * @param notificationType - Backend notification type
 * @returns Emoji string or React element for visual indicator
 */
export function getNotificationIcon(notificationType: string): string | React.ReactNode {
  // Cedar chat response → branded CC bubble icon (not an emoji)
  if (notificationType === 'chat_response_ready' || notificationType === 'background_response') {
    // Inline SVG keeps this lightweight and avoids circular dependency
    // with components/. Same path data as CCChatBubbleIcon.
    return React.createElement('svg', {
      width: 28,
      height: 28,
      viewBox: '0 0 363.38 279.57',
      xmlns: 'http://www.w3.org/2000/svg',
      style: { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 },
    },
      React.createElement('path', {
        d: 'M69.21,28.41h224.97c17.35,0,31.44,14.09,31.44,31.44v159.87c0,17.35-14.09,31.44-31.44,31.44H37.76V59.85c0-17.35,14.09-31.44,31.44-31.44Z',
        fill: '#1F6C4F',
      }),
      React.createElement('g', null,
        React.createElement('path', {
          d: 'M227.84,169.23c4.7,0,8.82-.65,12.34-1.95,3.53-1.3,6.09-2.59,7.7-3.86l5.57,11.13c-1.86,1.65-5.1,3.32-9.71,5.01-4.62,1.69-10.14,2.54-16.58,2.54-6.02,0-11.63-1.07-16.82-3.22-5.2-2.14-9.76-5.12-13.67-8.94-3.92-3.82-6.97-8.28-9.15-13.39-2.18-5.11-3.28-10.66-3.28-16.64s1.09-11.54,3.28-16.7c2.18-5.15,5.23-9.65,9.12-13.49,3.9-3.83,8.45-6.83,13.67-9,5.21-2.16,10.83-3.25,16.85-3.25,6.43,0,11.96.85,16.58,2.54,4.62,1.69,7.86,3.36,9.71,5.01l-5.57,11.13c-1.61-1.28-4.17-2.57-7.7-3.87-3.52-1.3-7.64-1.95-12.34-1.95-4.45,0-8.49.74-12.12,2.23-3.63,1.48-6.74,3.57-9.34,6.25-2.6,2.68-4.6,5.81-6,9.37-1.4,3.57-2.1,7.41-2.1,11.53s.7,8.04,2.1,11.63c1.4,3.59,3.4,6.72,6,9.4,2.6,2.68,5.71,4.76,9.34,6.25,3.63,1.48,7.67,2.23,12.12,2.23Z',
          fill: '#FFFFFF', stroke: '#FFFFFF', strokeMiterlimit: '10', strokeWidth: '1.45',
        }),
        React.createElement('path', {
          d: 'M172.68,164.03c-1.61,1.08-3.85,2.16-6.81,3.25-3.52,1.3-7.64,1.95-12.34,1.95-4.45,0-8.49-.74-12.12-2.23-3.63-1.49-6.74-3.57-9.34-6.25-2.6-2.68-4.6-5.81-6-9.4-1.4-3.59-2.1-7.46-2.1-11.63s.7-7.97,2.1-11.53c1.4-3.57,3.4-6.69,6-9.37,2.6-2.68,5.71-4.76,9.34-6.25,3.63-1.48,7.67-2.23,12.12-2.23,4.7,0,8.82.65,12.34,1.95,3.02,1.11,5.29,2.22,6.9,3.31,1.75-3.81,3.86-7.35,6.33-10.61-1.86-1.64-5.08-3.29-9.67-4.98-4.62-1.69-10.14-2.54-16.58-2.54-6.02,0-11.64,1.08-16.85,3.25-5.22,2.16-9.77,5.17-13.67,9-3.9,3.84-6.94,8.33-9.12,13.49-2.18,5.15-3.28,10.72-3.28,16.7s1.09,11.53,3.28,16.64c2.18,5.11,5.24,9.58,9.15,13.39,3.92,3.81,8.48,6.79,13.67,8.94,5.19,2.15,10.8,3.22,16.82,3.22,6.43,0,11.96-.85,16.58-2.54,4.54-1.66,7.73-3.3,9.6-4.92-2.48-3.26-4.6-6.79-6.34-10.6Z',
          fill: '#FFFFFF', stroke: '#FFFFFF', strokeMiterlimit: '10', strokeWidth: '1.45',
        })
      )
    );
  }

  const iconMap: Record<string, string> = {
    // Nudges
    'morning_checkin': '☀️',
    'workout_reminder': '🏃',
    'health_checkin': '❤️',

    // Goal/Plan events
    'goal_progress': '🎯',
    'goal_completed': '🏆',
    'goal_stall_check_in': '🔄',
    'goal_approaching_completion': '🎯',
    'gentle_second_goal': '✨',

    // Plan events
    'plan_generated': '🎉',
    'plan_updated': '🔄',
    'weekly_plan_ready': '📅',

    // Reports
    'weekly_report': '📊',

    // Debrief
    'post_workout_debrief': '💪',

    // Discovery
    'discovery_adjustment_check_in': '💬',

    // Milestones
    'milestone_achievement': '⭐',
  };

  return iconMap[notificationType] || '💬';
}

/**
 * Get notification title based on type
 * 
 * @param notificationType - Backend notification type
 * @returns Title string for notification
 */
export function getNotificationTitle(notificationType: string): string {
  const titleMap: Record<string, string> = {
    'morning_checkin': 'Coach Cedar',
    'workout_reminder': 'Workout Reminder',
    'health_checkin': 'Health Check-In',
    'goal_progress': 'Goal Progress',
    'goal_completed': 'Goal Completed',
    'plan_generated': 'Plan Ready',
    'plan_updated': 'Plan Updated',
    'weekly_plan_ready': 'Weekly Plan Ready',
    'weekly_report': 'Your Weekly Report',
    'post_workout_debrief': 'Coach Cedar',
    'milestone_achievement': 'Milestone Achievement',
    'background_response': 'Coach Cedar'
  };

  return titleMap[notificationType] || 'Coach Cedar';
}

/**
 * Truncate message for banner display
 * 
 * @param fullMessage - Complete message text
 * @param maxLength - Maximum length (default 100)
 * @returns Truncated message with ellipsis
 */
export function getBannerMessage(fullMessage: string, maxLength: number = 100): string {
  if (!fullMessage || fullMessage.length <= maxLength) {
    return fullMessage || '';
  }

  // Truncate at word boundary to avoid cutting words
  const truncated = fullMessage.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace === -1) {
    // No spaces found, just truncate
    return truncated + '...';
  }

  return truncated.substring(0, lastSpace) + '...';
}

/**
 * Check if notification should show banner when user is in-app
 * Helper for notification polling logic
 * 
 * @param notificationType - Backend notification type
 * @param isOnChat - Is user currently on chat screen?
 * @returns true if should show banner
 */
export function shouldShowBanner(notificationType: string, isOnChat: boolean): boolean {
  // Never show banner if on chat (just add pill)
  if (isOnChat) {
    return false;
  }

  // Show banner for banner types
  return getNotificationBehavior(notificationType) === 'banner';
}

/**
 * Check if notification should trigger auto-navigate
 * 
 * @param notificationType - Backend notification type
 * @param isOnChat - Is user currently on chat screen?
 * @returns true if should auto-navigate
 */
export function shouldAutoNavigate(notificationType: string, isOnChat: boolean): boolean {
  // Never auto-navigate if already on chat
  if (isOnChat) {
    return false;
  }

  // Only post-workout debrief auto-navigates
  return getNotificationBehavior(notificationType) === 'auto';
}
